import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../services/api";
import Button from "../components/ui/Button";
import PageSkeleton from "../components/ui/PageSkeleton";
import {
  ArrowBack,
  Edit,
  Work,
  LocationOn,
  Business,
  AttachMoney,
  CalendarToday,
  Description,
  CheckCircle,
  People,
  Visibility,
} from "@mui/icons-material";

export default function JobPostingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  useEffect(() => {
    fetchJob();
    fetchCandidates();
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/recruitment/jobs/${id}`);
      setJob(response.data);
    } catch (err) {
      console.error("Failed to fetch job", err);
      toast.error("Failed to load job posting");
      navigate("/recruitment");
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      setCandidatesLoading(true);
      const response = await api.get("/recruitment/candidates", {
        params: { job_posting_id: id },
      });
      const candidatesData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setCandidates(candidatesData);
    } catch (err) {
      console.error("Failed to fetch candidates", err);
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "bg-gray-100 text-gray-700",
      published: "bg-green-100 text-green-700",
      closed: "bg-red-100 text-red-700",
    };
    return badges[status] || badges.draft;
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (!job) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/recruitment")}
            className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
          >
            <ArrowBack className="w-5 h-5" />
            <span className="font-medium">Back to Recruitment</span>
          </button>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate(`/recruitment/jobs/${id}/edit`)}
            >
              <Edit className="w-5 h-5" />
              Edit
            </Button>
          </div>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-2">
              {job.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-600">
              <span className="flex items-center gap-1">
                <Business className="w-4 h-4" />
                {job.department_id?.name || "N/A"}
              </span>
              <span className="flex items-center gap-1">
                <LocationOn className="w-4 h-4" />
                {job.location || "Not specified"}
              </span>
              <span className="capitalize">{job.employment_type}</span>
              {job.experience && (
                <span className="flex items-center gap-1">
                  <Work className="w-4 h-4" />
                  {job.experience}
                </span>
              )}
            </div>
          </div>
          <span
            className={`px-4 py-2 rounded-xl text-sm font-semibold ${getStatusBadge(
              job.status
            )}`}
          >
            {job.status.toUpperCase()}
          </span>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
          >
            <h2 className="text-2xl font-bold text-primary mb-4 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <Description className="w-6 h-6 text-primary" />
              </div>
              Job Description
            </h2>
            <div className="prose max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">
                {job.description || "No description provided."}
              </p>
            </div>
          </motion.div>

          {/* Key Responsibilities */}
          {job.responsibilities && job.responsibilities.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <h2 className="text-2xl font-bold text-primary mb-4">
                Key Responsibilities
              </h2>
              <ul className="space-y-2">
                {job.responsibilities.map((resp, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{resp}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Required Skills & Qualifications */}
          {job.required_skills && job.required_skills.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card"
            >
              <h2 className="text-2xl font-bold text-primary mb-4">
                Required Skills & Qualifications
              </h2>
              <ul className="space-y-2">
                {job.required_skills.map((skill, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{skill}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Nice to Have */}
          {job.nice_to_have && job.nice_to_have.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card"
            >
              <h2 className="text-2xl font-bold text-primary mb-4">
                Nice to Have
              </h2>
              <ul className="space-y-2">
                {job.nice_to_have.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary/60 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* What We Offer */}
          {job.what_we_offer && job.what_we_offer.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="card"
            >
              <h2 className="text-2xl font-bold text-primary mb-4">
                What We Offer
              </h2>
              <ul className="space-y-2">
                {job.what_we_offer.map((offer, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{offer}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Job Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <h3 className="text-xl font-bold text-primary mb-4">Job Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Department</p>
                <p className="font-semibold text-gray-900">
                  {job.department_id?.name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Location</p>
                <p className="font-semibold text-gray-900">
                  {job.location || "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Employment Type</p>
                <p className="font-semibold text-gray-900 capitalize">
                  {job.employment_type}
                </p>
              </div>
              {job.experience && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Experience</p>
                  <p className="font-semibold text-gray-900">
                    {job.experience}
                  </p>
                </div>
              )}
              {job.salary_range &&
                (job.salary_range.min || job.salary_range.max) && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Salary Range</p>
                    <p className="font-semibold text-gray-900">
                      {job.salary_range.min
                        ? `₹${job.salary_range.min.toLocaleString()}`
                        : ""}
                      {job.salary_range.min && job.salary_range.max
                        ? " - "
                        : ""}
                      {job.salary_range.max
                        ? `₹${job.salary_range.max.toLocaleString()}`
                        : ""}
                    </p>
                  </div>
                )}
              <div>
                <p className="text-sm text-gray-500 mb-1">Posted Date</p>
                <p className="font-semibold text-gray-900">
                  {new Date(
                    job.posted_date || job.created_at
                  ).toLocaleDateString()}
                </p>
              </div>
              {job.posted_by && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Posted By</p>
                  <p className="font-semibold text-gray-900">
                    {job.posted_by?.name || "Unknown"}
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Statistics Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card"
          >
            <h3 className="text-xl font-bold text-primary mb-4">Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 flex items-center gap-2">
                  <Visibility className="w-5 h-5" />
                  Views
                </span>
                <span className="font-bold text-primary">
                  {job.views_count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 flex items-center gap-2">
                  <People className="w-5 h-5" />
                  Applications
                </span>
                <span className="font-bold text-primary">
                  {job.applications_count || 0}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Candidates Preview */}
          {candidates.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card"
            >
              <h3 className="text-xl font-bold text-primary mb-4">
                Recent Applications
              </h3>
              <div className="space-y-3">
                {candidates.slice(0, 5).map((candidate) => (
                  <div
                    key={candidate._id}
                    className="p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() =>
                      navigate(`/recruitment/candidates/${candidate._id}`)
                    }
                  >
                    <p className="font-semibold text-gray-900">
                      {candidate.first_name} {candidate.last_name}
                    </p>
                    <p className="text-sm text-gray-600">{candidate.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(
                        candidate.application_date
                      ).toLocaleDateString()}
                    </p>
                  </div>
                ))}
                {candidates.length > 5 && (
                  <button
                    onClick={() =>
                      navigate("/recruitment", {
                        state: { activeTab: "candidates", jobId: id },
                      })
                    }
                    className="w-full btn-secondary text-sm"
                  >
                    View All ({candidates.length})
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
